/**
 * StatTile
 * Classification: custom
 *
 * A headline figure with optional delta and trend. This is what `chartType:
 * 'kpi'` renders — a number is not a chart, and rendering one as an ECharts
 * instance with a faked title costs a canvas for no benefit.
 */
import type { ReactNode } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { chartTokens } from '@/tokens/chart';
import { severityColorsByMode } from '@/tokens/colors';
import { Sparkline } from '@/atoms/Sparkline';

export interface StatTileProps {
  value: ReactNode;
  label: string;
  /** Secondary context, e.g. "vs. previous 30 days". */
  caption?: string;
  /** Signed percentage change. Direction is conveyed by an arrow, not colour alone. */
  delta?: number;
  /**
   * Whether a rising delta is good. Drives the delta colour; the arrow and sign
   * carry the direction independently.
   */
  deltaPolarity?: 'higherIsBetter' | 'lowerIsBetter';
  trend?: readonly number[];
}

export function StatTile({
  value,
  label,
  caption,
  delta,
  deltaPolarity = 'higherIsBetter',
  trend,
}: StatTileProps) {
  const theme = useTheme();
  const mode = theme.palette.mode === 'light' ? 'light' : 'dark';
  const tokens = chartTokens[mode];
  const severity = severityColorsByMode[mode];

  const hasDelta = typeof delta === 'number' && Number.isFinite(delta);
  const rising = hasDelta && delta > 0;
  const good = deltaPolarity === 'higherIsBetter' ? rising : !rising;
  const deltaColor =
    !hasDelta || delta === 0 ? tokens.ink.label : good ? severity.info : severity.high;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h3" sx={{ color: 'text.primary', lineHeight: 1 }}>
          {value}
        </Typography>
        {hasDelta && (
          <Typography component="span" variant="body2" sx={{ color: deltaColor, fontWeight: 600 }}>
            {/* The arrow and sign carry direction; colour is reinforcement only. */}
            {delta > 0 ? '▲' : delta < 0 ? '▼' : '—'} {Math.abs(delta).toFixed(1)}%
          </Typography>
        )}
      </Box>
      {caption && (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {caption}
        </Typography>
      )}
      {trend && trend.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <Sparkline data={trend} variant="area" label={`${label} trend`} width={120} height={28} />
        </Box>
      )}
    </Box>
  );
}
